import { startTransition, useDebugValue, useEffect, useReducer, useRef } from 'react';
/**
 * A derived, reactive view over a collection. The selector receives a
 * snapshot of all visible entities and returns a projection (filter / map /
 * sort). Recomputes when the collection changes; re-renders only when the
 * projected result differs from the previous one (per `isEqual`, default
 * reference equality on the result).
 *
 * For single-record reads prefer `useCollectionItem` — it is O(1) and avoids
 * the full-collection scan this hook performs.
 */ export function useLiveQuery(collection, selector, deps = [], isEqual = Object.is) {
    const [, forceRender] = useReducer((n)=>n + 1, 0);
    const selectorRef = useRef(selector);
    selectorRef.current = selector;
    const isEqualRef = useRef(isEqual);
    isEqualRef.current = isEqual;
    // Compute the current result and cache it for stable identity.
    const resultRef = useRef(undefined);
    const initialized = useRef(false);
    if (!initialized.current) {
        resultRef.current = selector(collection.getAll());
        initialized.current = true;
    }
    useEffect(()=>{
        // Recompute on (re)subscribe to capture changes since last render.
        const next = selectorRef.current(collection.getAll());
        if (!isEqualRef.current(resultRef.current, next)) {
            resultRef.current = next;
            forceRender();
        }
        const unsubscribe = collection.subscribeToChanges(()=>{
            const recomputed = selectorRef.current(collection.getAll());
            if (!isEqualRef.current(resultRef.current, recomputed)) {
                resultRef.current = recomputed;
                // Non-blocking: a collection edit must not block the interaction/paint
                // while this derived view re-renders. React renders the new result at
                // low priority and can interrupt it for urgent updates. (The selector
                // itself still runs synchronously — prefer `useCollectionQuery` with an
                // index-backed select over `getAll()` for large collections.)
                startTransition(()=>forceRender());
            }
        });
        return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        collection,
        ...deps
    ]);
    useDebugValue(resultRef.current);
    return resultRef.current;
}
